import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

// Layouts
import AppLayout from '@/components/layout/AppLayout.vue';

// Auth pages: eager-loaded so redirect-to-login works even when CDN returns HTML for chunk 404s
import LoginPage from '@/pages/auth/LoginPage.vue';
import SignupPage from '@/pages/auth/SignupPage.vue';

// Lazy-loaded components
const DashboardPage = () => import('@/pages/dashboard/DashboardPage.vue');
// Placeholder component for 404 page
const NotFoundPage = () => import('@/components/common/NotFoundPage.vue');

// User pages
const UserListPage = () => import('@/pages/users/UserListPage.vue');
const UserAddPage = () => import('@/pages/users/UserAddPage.vue');
const UserDetailPage = () => import('@/pages/users/UserDetailPage.vue');
const UserEditPage = () => import('@/pages/users/UserEditPage.vue');
const PatientTimelinePage = () => import('@/pages/users/PatientTimelinePage.vue');

// Resource pages
const ResourceListPage = () => import('@/pages/resources/ResourceListPage.vue');
const ResourceAddPage = () => import('@/pages/resources/ResourceAddPage.vue');
const ResourceDetailPage = () => import('@/pages/resources/ResourceDetailPage.vue');
const ResourceEditPage = () => import('@/pages/resources/ResourceEditPage.vue');

// Appointment pages
const AppointmentCalendarPage = () => import('@/pages/appointments/AppointmentCalendarPage.vue');
const AppointmentAddPage = () => import('@/pages/appointments/AppointmentAddPage.vue');
const AppointmentDetailPage = () => import('@/pages/appointments/AppointmentDetailPage.vue');
const AppointmentEditPage = () => import('@/pages/appointments/AppointmentEditPage.vue');

// Chat pages
const ChatConversationPage = () => import('@/pages/chat/ChatConversationPage.vue');

// Scheduled Message pages
const ScheduledMessageListPage = () =>
  import('@/pages/scheduledMessages/ScheduledMessageListPage.vue');
const ScheduledMessageAddPage = () =>
  import('@/pages/scheduledMessages/ScheduledMessageAddPage.vue');
const ScheduledMessageEditPage = () =>
  import('@/pages/scheduledMessages/ScheduledMessageEditPage.vue');

// Reports page
const ReportsPage = () => import('@/pages/reports/ReportsPage.vue');

// Rules page
const RuleListPage = () => import('@/pages/rules/RuleListPage.vue');

// Settings page (Configuración: Google y más secciones en el futuro)
const SettingsPage = () => import('@/pages/settings/SettingsPage.vue');

const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/signup',
    name: 'Signup',
    component: SignupPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/book/manage',
    name: 'ManageAppointment',
    component: () => import('@/pages/booking/ManageAppointmentPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/book/:resourceId?',
    name: 'PublicBooking',
    component: () => import('@/pages/booking/PublicBookingPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/tenant-error',
    name: 'TenantError',
    component: () => import('@/pages/tenant/TenantErrorPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: () => import('@/pages/legal/PrivacyPolicyPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/terms',
    name: 'TermsOfService',
    component: () => import('@/pages/legal/TermsOfServicePage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: AppLayout,
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: DashboardPage,
        meta: { requiresAuth: true },
        alias: '/dashboard',
      },
      {
        path: 'users',
        name: 'UserList',
        component: UserListPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'users/add',
        name: 'UserAdd',
        component: UserAddPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'users/:id',
        name: 'UserDetail',
        component: UserDetailPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'users/:id/edit',
        name: 'UserEdit',
        component: UserEditPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'users/:id/timeline',
        name: 'PatientTimeline',
        component: PatientTimelinePage,
        meta: { requiresAuth: true },
      },
      {
        path: 'resources',
        name: 'ResourceList',
        component: ResourceListPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'resources/add',
        name: 'ResourceAdd',
        component: ResourceAddPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'resources/:id',
        name: 'ResourceDetail',
        component: ResourceDetailPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'resources/:id/edit',
        name: 'ResourceEdit',
        component: ResourceEditPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'resources/:id/availability',
        name: 'ResourceAvailability',
        component: () => import('@/pages/resources/ResourceAvailabilityPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'appointments',
        name: 'AppointmentCalendar',
        component: AppointmentCalendarPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'appointments/add',
        name: 'AppointmentAdd',
        component: AppointmentAddPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'appointments/:id',
        name: 'AppointmentDetail',
        component: AppointmentDetailPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'appointments/:id/edit',
        name: 'AppointmentEdit',
        component: AppointmentEditPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'chat',
        name: 'ChatConversation',
        component: ChatConversationPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'scheduled-messages',
        name: 'ScheduledMessageList',
        component: ScheduledMessageListPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'scheduled-messages/add',
        name: 'ScheduledMessageAdd',
        component: ScheduledMessageAddPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'scheduled-messages/:id/edit',
        name: 'ScheduledMessageEdit',
        component: ScheduledMessageEditPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'reports',
        name: 'Reports',
        component: ReportsPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'rules',
        name: 'RuleList',
        component: RuleListPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'settings',
        name: 'Settings',
        component: SettingsPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'settings/integrations',
        redirect: (to) => ({ path: '/settings', query: to.query }),
      },
      {
        path: 'settings/integrations/google',
        redirect: (to) => ({ path: '/settings', query: to.query }),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundPage,
  },
];

console.log('BASE_URL:', import.meta.env.BASE_URL);

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL || '/'),
  routes,
});

router.beforeEach((to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false);

  // Get auth state from Pinia store
  const authStore = useAuthStore();
  const currentUser = authStore.user;
  const token = authStore.token;
  const isAuthenticated = authStore.isAuthenticated;

  console.log('Navigation Guard:', {
    to: to.path,
    from: from.path,
    requiresAuth,
    currentUser: !!currentUser,
    token: !!token,
    isAuthenticated,
    user: currentUser,
  });

  if (requiresAuth && !isAuthenticated) {
    // Redirect to login if not authenticated
    console.log('Redirecting to login - not authenticated');
    next('/login');
  } else if (requiresAuth && isAuthenticated && !authStore.hasUiAccess) {
    // Authenticated but no UI access (e.g. ROLE_USER only) → clear session and redirect to login
    authStore.logout();
    next('/login');
  } else if ((to.path === '/login' || to.path === '/signup') && isAuthenticated) {
    if (authStore.hasUiAccess) {
      console.log('Redirecting to dashboard - already authenticated');
      next('/dashboard');
    } else {
      // Authenticated but no UI access (e.g. ROLE_USER) → clear session so login page is fresh
      authStore.logout();
      next();
    }
  } else {
    console.log('Navigation allowed');
    next();
  }
});

export default router;
